<template>
  <div class="chart-container">
    <canvas 
      v-show="data && data.length > 0" 
      ref="chartCanvas"
    ></canvas>
    <div v-if="!data || data.length === 0" class="no-data-message">
      <div class="text-center text-muted py-5">
        <i class="bi bi-graph-up" style="font-size: 3rem; opacity: 0.3;"></i>
        <p class="mt-3 mb-0">Нет данных для отображения графика</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  LineController,
  BarController,
  Title,
  Tooltip,
  Legend
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  LineController,
  BarController,
  Title,
  Tooltip,
  Legend
)

export default {
  name: 'CostChart',
  props: {
    data: {
      type: Array,
      required: true,
      default: () => []
    },
    chartType: {
      type: String,
      default: 'line',
      validator: value => ['line', 'bar'].includes(value)
    }
  },
  data() {
    return {
      chart: null
    }
  },
  mounted() {
    this.initChart()
  },
  watch: {
    data: {
      handler() {
        this.updateChartData()
      },
      deep: true
    },
    chartType: {
      handler() {
        this.recreateChart()
      }
    }
  },
  beforeUnmount() {
    this.destroyChart()
  },
  methods: {
    destroyChart() {
      if (this.chart) {
        try {
          this.chart.destroy()
        } catch (e) {
          // Игнорируем ошибки при уничтожении
        }
        this.chart = null
      }
    },
    
    initChart() {
      this.$nextTick(() => {
        if (!this.data || this.data.length === 0) return
        if (!this.$refs.chartCanvas) return
        
        this.createChart()
      })
    },
    
    recreateChart() {
      this.destroyChart()
      this.$nextTick(() => {
        this.initChart()
      })
    },
    
    createChart() {
      try {
        const ctx = this.$refs.chartCanvas?.getContext('2d')
        if (!ctx) return
        
        const chartData = this.getChartData()
        
        this.chart = new ChartJS(ctx, {
          type: this.chartType,
          data: chartData,
          options: this.getChartOptions()
        })
      } catch (error) {
        console.error('Error creating chart:', error)
      }
    },
    
    updateChartData() {
      if (!this.chart) {
        this.initChart()
        return
      }
      
      try {
        const newData = this.getChartData()
        this.chart.data = newData
        this.chart.update('none')
      } catch (error) {
        console.error('Error updating chart:', error)
        this.recreateChart()
      }
    },
    
    getChartData() {
      if (!this.data || this.data.length === 0) {
        return { labels: [], datasets: [] }
      }
      
      const labels = this.data.map(item => this.formatDate(item.date))
      const values = this.data.map(item => item.total_cost || 0)
      
      const dataset = {
        label: 'Расходы',
        data: values,
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: this.chartType === 'line' 
          ? 'rgba(54, 162, 235, 0.1)' 
          : 'rgba(54, 162, 235, 0.6)',
        borderWidth: 2
      }
      
      // Добавляем свойства только для линейного графика
      if (this.chartType === 'line') {
        dataset.tension = 0.4
        dataset.pointRadius = 4
        dataset.pointHoverRadius = 6
        dataset.pointBackgroundColor = 'rgb(54, 162, 235)'
        dataset.pointBorderColor = '#fff'
        dataset.pointBorderWidth = 2
        // Убираем fill для предотвращения ошибок
      }
      
      return {
        labels,
        datasets: [dataset]
      }
    },
    
    getChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false, // Отключаем анимации для стабильности
        plugins: {
          title: {
            display: true,
            text: 'Расходы по дням',
            font: {
              size: 16,
              weight: 'bold'
            }
          },
          legend: {
            display: false
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (context) => {
                return `Потрачено: $${context.parsed.y.toFixed(2)}`
              }
            }
          }
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: 'Дата'
            },
            grid: {
              display: false
            }
          },
          y: {
            display: true,
            title: {
              display: true,
              text: 'Сумма (USD)'
            },
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                return '$' + value.toFixed(2)
              }
            }
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        }
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.chart-container {
  position: relative;
  height: 400px;
  width: 100%;
}
</style> 